import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { authService, OAuthLoginResponse, RegisterRequest } from '../../services/auth';
import type { AccountType } from '../../types/recruiter';
import { useIsMobile } from '../../hooks/useIsMobile';
import { MobileWarningBanner } from '../MobileWarningBanner';
import { SocialLoginButtons } from './SocialLoginButtons';

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after any successful login / registration / OAuth. */
  onAuthenticated: () => void;
  /** Partner code attached to registrations (e.g. vt_veterans). */
  partner?: string;
  /**
   * Recruiter invite token (from /signup?invite=<token>). When present the
   * account-type choice is locked to candidate and the token is sent with
   * the registration so the invite link activates and credits are granted.
   */
  inviteToken?: string;
  /** Preselected account type in register mode (e.g. from /signup?type=recruiter). */
  defaultAccountType?: AccountType;
  /** Which form the dialog shows when it opens (default: sign in). */
  initialMode?: 'signin' | 'register';
}

/**
 * The single login/register dialog, shared by LandingLayout, Header, and
 * SignupPage (previously two duplicate hand-rolled modals).
 */
export const AuthDialog: React.FC<AuthDialogProps> = ({
  open,
  onOpenChange,
  onAuthenticated,
  partner,
  inviteToken,
  defaultAccountType = 'candidate',
  initialMode = 'signin',
}) => {
  const isMobile = useIsMobile();
  const [showMobileWarning, setShowMobileWarning] = useState(true);
  const [isRegistering, setIsRegistering] = useState(initialMode === 'register');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Invited signups are always candidates (the invite is a candidate link).
  const [accountType, setAccountType] = useState<AccountType>(
    inviteToken ? 'candidate' : defaultAccountType
  );
  const [agencyName, setAgencyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isRecruiterSignup = isRegistering && !inviteToken && accountType === 'recruiter';

  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setAgencyName('');
    setError('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setIsRegistering(initialMode === 'register');
      setAccountType(inviteToken ? 'candidate' : defaultAccountType);
      resetForm();
      setShowMobileWarning(true);
    }
    onOpenChange(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isRegistering) {
        const registration: RegisterRequest = {
          name,
          email,
          password,
          ...(partner && { partner }),
          ...(inviteToken && { invite_token: inviteToken }),
          ...(isRecruiterSignup && {
            account_type: 'recruiter' as const,
            agency_name: agencyName,
          }),
        };
        await authService.register(registration);
        toast.success('Account created!', {
          description: 'Please check your email to verify your account.',
        });
      } else {
        await authService.login({ email, password });
      }
      handleOpenChange(false);
      onAuthenticated();
    } catch (err: any) {
      const message = err.message || (isRegistering ? 'Registration failed' : 'Login failed');
      toast.error(isRegistering ? 'Registration failed' : 'Login failed', {
        description: message,
      });
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSuccess = (response: OAuthLoginResponse) => {
    handleOpenChange(false);
    if (response.is_new_user) {
      toast.success('Welcome to Career Catalyst!', {
        description: 'Your account has been created.',
      });
    }
    onAuthenticated();
  };

  const toggleAuthMode = () => {
    setIsRegistering(!isRegistering);
    resetForm();
  };

  const registerDescription = inviteToken
    ? 'Your recruiter invited you. Create your account to connect and get started.'
    : isRecruiterSignup
      ? 'Set up your agency, build your roster, and track outcomes.'
      : 'Start with 3 free resume tailoring credits.';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isRegistering ? 'Create your account' : 'Sign in to Career Catalyst'}
          </DialogTitle>
          <DialogDescription>
            {isRegistering ? registerDescription : 'Welcome back. Pick up where you left off.'}
          </DialogDescription>
        </DialogHeader>

        {isMobile && showMobileWarning && (
          <MobileWarningBanner onDismiss={() => setShowMobileWarning(false)} />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {error}
            </div>
          )}
          {isRegistering && !inviteToken && (
            <Tabs
              value={accountType}
              onValueChange={(value) => setAccountType(value as AccountType)}
            >
              <TabsList className="w-full">
                <TabsTrigger value="candidate">I'm looking for a job</TabsTrigger>
                <TabsTrigger value="recruiter">I'm a recruiter</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          {isRegistering && (
            <div className="space-y-2">
              <Label htmlFor="auth-name">Name</Label>
              <Input
                id="auth-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                required
              />
            </div>
          )}
          {isRecruiterSignup && (
            <div className="space-y-2">
              <Label htmlFor="auth-agency-name">Agency name</Label>
              <Input
                id="auth-agency-name"
                type="text"
                value={agencyName}
                onChange={(e) => setAgencyName(e.target.value)}
                placeholder="Enter your agency name"
                required
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="auth-email">Email</Label>
            <Input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="auth-password">Password</Label>
            <Input
              id="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" size="lg" disabled={isLoading} className="flex-1">
              {isLoading
                ? isRegistering
                  ? 'Creating account…'
                  : 'Signing in…'
                : isRegistering
                  ? 'Create account'
                  : 'Sign in'}
            </Button>
            <Button type="button" size="lg" variant="ghost" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </form>

        {/* OAuth cannot carry account_type or invite_token (register endpoint
            only), so hide it for recruiter signups and invited candidates. */}
        {!(isRegistering && (isRecruiterSignup || inviteToken)) && (
          <SocialLoginButtons
            onSuccess={handleOAuthSuccess}
            mode={isRegistering ? 'signup' : 'login'}
            showDivider={true}
          />
        )}

        <p className="text-center text-sm text-muted-foreground">
          {isRegistering ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            type="button"
            onClick={toggleAuthMode}
            className="font-medium text-primary hover:underline"
          >
            {isRegistering ? 'Sign in' : 'Create one'}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
